<!--
  One control shape for every small toggle and tag in the app: filter chips in the
  link picker, season tabs, genre tags. `as="button"` gives it the pressed state;
  the default is inert and reads as a tag.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    children: Snippet;
    /** Interactive chips get hover/pressed states and a real button role. */
    interactive?: boolean;
    selected?: boolean;
    disabled?: boolean;
    /** Semantic colouring for verification verdicts. */
    tone?: 'default' | 'good' | 'warn' | 'bad' | 'accent';
    title?: string;
    onclick?: () => void;
  }

  let {
    children,
    interactive = false,
    selected = false,
    disabled = false,
    tone = 'default',
    title,
    onclick
  }: Props = $props();
</script>

{#if interactive}
  <button
    type="button"
    class="chip {tone}"
    class:selected
    {disabled}
    {title}
    aria-pressed={selected}
    {onclick}
  >
    {@render children()}
  </button>
{:else}
  <span class="chip {tone}" class:selected {title}>{@render children()}</span>
{/if}

<style>
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border-radius: 99px;
    border: 1px solid var(--line);
    background: var(--surface-1);
    color: var(--text-dim);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.35;
    white-space: nowrap;
    letter-spacing: 0.01em;
  }

  button.chip {
    position: relative;
    transition:
      background-color 140ms var(--ease),
      border-color 140ms var(--ease),
      color 140ms var(--ease);
  }

  /**
   * A CHIP IS 26 PIXELS TALL AND A THUMB IS NOT.
   *
   * The season row on a series page is drawn out of these — "Season 1 … Specials",
   * measured at 76 x 26.2 — and it is the primary way of moving around a show. At
   * 60 % of the 44 px floor it is a mis-tap waiting to happen.
   *
   * The target is grown WITHOUT growing the chip, because the visual language of a
   * small pill is deliberate and a row of 44 px lozenges is a different design. The
   * pseudo-element extends the hit area vertically only: chips sit side by side in
   * a horizontal track, so nothing here can overlap its neighbour and steal its
   * press. Under a fine pointer there is nothing to fix and nothing is added.
   */
  @media (pointer: coarse) {
    button.chip::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      height: var(--hit);
      transform: translateY(-50%);
    }
  }
  button.chip:hover:not(:disabled) {
    background: var(--surface-3);
    color: var(--text);
  }
  button.chip:active:not(:disabled) {
    transform: translateY(0.5px);
  }
  button.chip:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .selected {
    background: color-mix(in oklab, var(--focus-ring) 16%, var(--surface-1));
    border-color: color-mix(in oklab, var(--focus-ring) 42%, var(--line));
    color: var(--text);
  }
  button.selected:hover {
    background: color-mix(in oklab, var(--focus-ring) 24%, var(--surface-1));
  }

  .good {
    color: var(--good);
    border-color: color-mix(in oklab, var(--good) 34%, var(--line));
    background: color-mix(in oklab, var(--good) 12%, var(--surface-1));
  }
  .warn {
    color: var(--warn);
    border-color: color-mix(in oklab, var(--warn) 34%, var(--line));
    background: color-mix(in oklab, var(--warn) 12%, var(--surface-1));
  }
  .bad {
    color: var(--bad);
    border-color: color-mix(in oklab, var(--bad) 30%, var(--line));
    background: color-mix(in oklab, var(--bad) 10%, var(--surface-1));
  }
  .accent {
    color: var(--accent-hot);
    border-color: color-mix(in oklab, var(--accent) 40%, var(--line));
    background: color-mix(in oklab, var(--accent) 14%, var(--surface-1));
  }
</style>
